import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Chip,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Stepper,
    Step,
    StepLabel,
    Divider,
    IconButton,
    Tooltip,
    Badge,
} from '@mui/material';
import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridToolbarExport,
    GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import {
    CloudUpload as CloudUploadIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon,
    School as SchoolIcon,
    Person as PersonIcon,
    AttachMoney as AttachMoneyIcon,
    Description as DescriptionIcon,
    AutoAwesome as AutoAwesomeIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const API_BASE = 'https://gpt5api.swancreate.com';
const SQL_API = 'https://figazuresql.swancreate.com/api/swa';

const LaborHoursUpload = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [statusDialog, setStatusDialog] = useState({ open: false, logs: [] });
    const [employeeData, setEmployeeData] = useState([]);
    const [schools, setSchools] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [stats, setStats] = useState({ total: 0, matched: 0, unmatched: 0 });
    const [selectionModel, setSelectionModel] = useState([]);
    const [exportProgress, setExportProgress] = useState({ current: 0, total: 0, type: '' });

    useEffect(() => {
        initializeData();
    }, []);

    useEffect(() => {
        if (selectedCategory) {
            fetchSubcategories(selectedCategory);
        } else {
            setSubcategories([]);
            setSelectedSubcategory('');
        }
    }, [selectedCategory]);

    const initializeData = async () => {
        addLog('🔄 Loading system data...', 'info');
        setStatusDialog({ open: true, logs: [] });

        try {
            const [schoolsRes, categoriesRes] = await Promise.all([
                axios.get(`${SQL_API}/SchoolMst`),
                axios.get(`${SQL_API}/FINANCE_Categories`)
            ]);

            const schoolData = schoolsRes.data || [];
            const categoryData = (categoriesRes.data || []).filter(c => c.Type === 'Expense');

            setSchools(schoolData);
            setCategories(categoryData);

            addLog(`✅ Loaded ${schoolData.length} schools`, 'success');
            addLog(`✅ Loaded ${categoryData.length} expense categories`, 'success');

            setTimeout(() => setStatusDialog({ open: false, logs: [] }), 2000);
        } catch (error) {
            addLog(`❌ Error loading data: ${error.message}`, 'error');
            console.error('Initialization error:', error);
        }
    };

    const fetchSubcategories = async (categoryId) => {
        try {
            const response = await axios.get(`${SQL_API}/FINANCE_Subcategories`);
            const filtered = (response.data || []).filter(s => s.CategoryID === parseInt(categoryId));
            setSubcategories(filtered);
            addLog(`✅ Loaded ${filtered.length} subcategories`, 'info');
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            addLog(`⚠️ Could not load subcategories: ${error.message}`, 'warning');
        }
    };

    const addLog = (message, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString();
        setStatusDialog(prev => ({
            ...prev,
            logs: [...prev.logs, { message, type, timestamp }]
        }));
    };

    const matchSchool = (locCode) => {
        if (!locCode) return null;
        const cleanLoc = locCode.toString().trim();

        let school = schools.find(s => s.SchoolCode === cleanLoc);
        if (school) return school;

        school = schools.find(s => s.SiteCode === cleanLoc);
        if (school) return school;

        return null;
    };

    const handleFileSelect = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.type === 'application/pdf') {
            setFile(selectedFile);
            setEmployeeData([]);
            setStats({ total: 0, matched: 0, unmatched: 0 });
            setActiveStep(1);
        } else {
            alert('Please select a PDF file');
        }
    };

    const processPayrollPDF = async () => {
        if (!file) return;

        setLoading(true);
        setStatusDialog({ open: true, logs: [] });
        addLog('📤 Uploading PDF to document intelligence...', 'info');

        try {
            const formData = new FormData();
            formData.append('file', file);

            addLog('🔍 Extracting document data...', 'info');

            const diResponse = await axios.post(
                `${API_BASE}/v1/di/extract`,
                formData,
                {
                    headers: {
                        'accept': 'application/json',
                        'Content-Type': 'multipart/form-data'
                    },
                    timeout: 180000
                }
            );

            addLog('✅ Document extracted successfully', 'success');

            const extractedData = diResponse.data;
            const tables = extractedData.tables || [];
            const pageCount = extractedData.page_count || extractedData.pages?.length || 0;

            addLog(`📊 Processing ${pageCount} pages with ${tables.length} tables...`, 'info');

            if (tables.length === 0) {
                addLog('⚠️ No tables found in document', 'warning');
                setLoading(false);
                return;
            }

            const employees = parseEmployeeDataFromTables(tables);

            addLog(`📋 Found ${employees.length} employee entries`, 'info');

            if (employees.length === 0) {
                addLog('⚠️ No employee data could be parsed from tables', 'warning');
                setLoading(false);
                return;
            }

            const matchedEmployees = employees.map(emp => {
                const school = matchSchool(emp.LOC);
                return {
                    ...emp,
                    SchoolId: school?.SchoolId || null,
                    SchoolName: school?.SchoolName || 'UNMATCHED',
                    matched: !!school
                };
            });

            const matched = matchedEmployees.filter(e => e.matched).length;
            const unmatched = matchedEmployees.length - matched;

            setStats({ total: matchedEmployees.length, matched, unmatched });
            setEmployeeData(matchedEmployees);
            setActiveStep(2);

            addLog(`✅ Matching complete: ${matched} matched, ${unmatched} unmatched`,
                matched === matchedEmployees.length ? 'success' : 'warning');

            if (unmatched > 0) {
                addLog(`⚠️ Review unmatched records before export`, 'warning');
            }

            setTimeout(() => setStatusDialog({ open: false, logs: [] }), 3000);

        } catch (error) {
            console.error('PDF Processing Error:', error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.message || 'Unknown error';
            addLog(`❌ Error: ${errorMsg}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const parseEmployeeDataFromTables = (tables) => {
        const employeeMap = new Map();
        const periodEndDate = '08/24/2025';

        tables.forEach((table, tableIdx) => {
            const rows = table.rows || [];

            for (let i = 0; i < rows.length; i++) {
                const row = rows[i] || [];
                const cells = row.map(c => (c || '').toString().trim());

                if (cells[0] === 'NAME KEY' || cells[0] === 'PERIOD END' || cells[0] === 'HOURS') {
                    continue;
                }

                if (cells[0] && cells[0].endsWith('000') && cells[0].length > 5) {
                    const nameKey = cells[0];

                    let employeeName = '';
                    let nameEndIdx = 1;
                    for (let j = 1; j < cells.length && j < 6; j++) {
                        const cell = cells[j];
                        if (/^(1895|1896|1898|2P|2E|13|05|X09A|13B|1895B|1896B|1898B)$/.test(cell)) {
                            nameEndIdx = j;
                            break;
                        }
                        if (cell && cell !== '') {
                            employeeName += (employeeName ? ' ' : '') + cell;
                        }
                    }

                    if (!employeeName) continue;

                    let locCode = null;
                    for (let k = nameEndIdx + 1; k < cells.length && k < 10; k++) {
                        if (/^\d{4}$/.test(cells[k])) {
                            locCode = cells[k];
                            break;
                        }
                    }

                    if (!locCode) continue;

                    const key = `${nameKey}_${locCode}`;
                    if (!employeeMap.has(key)) {
                        employeeMap.set(key, {
                            id: uuidv4(),
                            nameKey,
                            employeeName: employeeName.trim(),
                            periodEndDate,
                            LOC: locCode,
                            totalHours: 0,
                            totalGross: 0,
                            entries: []
                        });
                    }

                    const employee = employeeMap.get(key);

                    for (let j = i + 1; j < Math.min(i + 15, rows.length); j++) {
                        const payRow = rows[j] || [];
                        const payCells = payRow.map(c => (c || '').toString().trim());

                        if (payCells[0] && payCells[0].endsWith('000') && payCells[0].length > 5) {
                            break;
                        }

                        if (payCells[0] && /^\d{2}\/\d{2}\/\d{4}$/.test(payCells[0])) {
                            const entryDate = payCells[0];

                            let hours = 0;
                            let gross = 0;

                            for (let k = 5; k < payCells.length; k++) {
                                const cell = payCells[k];
                                const cleaned = cell.replace(/[$,]/g, '');
                                const val = parseFloat(cleaned);

                                if (!isNaN(val) && val > 100 && /^\d+\.\d{2}$/.test(cleaned)) {
                                    gross = val;
                                    break;
                                }
                            }

                            for (let k = 5; k < payCells.length; k++) {
                                const cell = payCells[k];
                                const cleaned = cell.replace(/,/g, '');
                                const val = parseFloat(cleaned);

                                if (!isNaN(val) && val > 0 && val < 200 && val !== gross) {
                                    if (val !== 1.0 && val !== 0.5 && val !== 0.25 && val !== 1.5 && val !== 2.0) {
                                        hours = val;
                                    }
                                }
                            }

                            if (hours === 0 && j + 1 < rows.length) {
                                const nextRow = rows[j + 1] || [];
                                const nextCells = nextRow.map(c => (c || '').toString().trim());

                                if (nextCells[0] && /^\d+\.\d+$/.test(nextCells[0]) && !nextCells[0].includes('/')) {
                                    const val = parseFloat(nextCells[0]);
                                    if (val > 0 && val < 200) {
                                        hours = val;
                                    }
                                }
                            }

                            if (hours > 0 || gross > 0) {
                                employee.entries.push({
                                    date: entryDate,
                                    LOC: locCode,
                                    hours,
                                    gross
                                });

                                employee.totalHours += hours;
                                employee.totalGross += gross;
                            }
                        }
                    }
                }
            }
        });

        return Array.from(employeeMap.values()).filter(emp =>
            emp.totalHours > 0 || emp.totalGross > 0
        );
    };

    const exportToDatabase = async () => {
        if (!selectedCategory || !selectedSubcategory) {
            alert('❌ Please select Category and Subcategory before exporting');
            return;
        }

        const selectedRows = selectionModel.length > 0
            ? employeeData.filter(emp => selectionModel.includes(emp.id))
            : employeeData;

        const matchedRows = selectedRows.filter(emp => emp.matched);

        if (matchedRows.length === 0) {
            alert('❌ No matched employees to export');
            return;
        }

        const categoryObj = categories.find(c => c.CategoryID === parseInt(selectedCategory));
        const subcategoryObj = subcategories.find(s => s.SubcategoryID === parseInt(selectedSubcategory));

        if (!categoryObj || !subcategoryObj) {
            alert('❌ Could not find category or subcategory information');
            return;
        }

        setLoading(true);
        setStatusDialog({ open: true, logs: [] });
        addLog('🚀 Starting export process...', 'info');
        addLog(`📋 Category: ${categoryObj.CategoryName} (ID: ${categoryObj.CategoryID})`, 'info');
        addLog(`📋 Subcategory: ${subcategoryObj.SubcategoryName} (ID: ${subcategoryObj.SubcategoryID})`, 'info');

        try {
            const timestamp = Date.now();
            let laborSuccess = 0;
            let expenseSuccess = 0;
            let totalLabor = 0;
            let totalExpense = 0;

            for (let empIdx = 0; empIdx < matchedRows.length; empIdx++) {
                const emp = matchedRows[empIdx];

                for (let entryIdx = 0; entryIdx < emp.entries.length; entryIdx++) {
                    const entry = emp.entries[entryIdx];
                    const entryDate = new Date(entry.date);

                    const laborRecord = {
                        SchoolId: emp.SchoolId ? emp.SchoolId.toString() : null,
                        SchoolName: emp.SchoolName ? emp.SchoolName.toString() : null,
                        LaborHours: entry.hours,
                        Date: entryDate.toISOString().split('T')[0],
                        CreatedOn: new Date().toISOString(),
                        UpdatedOn: new Date().toISOString(),
                        isActive: true,
                        Type: 'Payroll',
                        Month: (entryDate.getMonth() + 1).toString(),
                        Year: entryDate.getFullYear().toString()
                    };

                    const expenseRecord = {
                        ProcessID: `PAYROLL_${timestamp}_${empIdx}_${entryIdx}`,
                        ExpenseDate: entryDate.toISOString(),
                        Amount: entry.gross,
                        ExpenseCategoryID: categoryObj.CategoryID.toString(),
                        ExpenseSubCategoryID: subcategoryObj.SubcategoryID.toString(),
                        ExpenseCategory: categoryObj.CategoryName,
                        ExpenseSubcategory: subcategoryObj.SubcategoryName,
                        Description: `${emp.employeeName} - ${entry.hours} hrs`,
                        isActive: true,
                        CreatedOn: new Date().toISOString(),
                        UpdatedOn: new Date().toISOString(),
                        SchoolId: emp.SchoolId ? emp.SchoolId.toString() : null,
                        SiteName: emp.SchoolName ? emp.SchoolName.toString() : null,
                        LaborHours: entry.hours,
                        catFlag: true,
                        isInvoice: false,
                        isCategorized: true,
                        isScanned: false,
                        isPaid: false,
                        isVariance: false
                    };

                    totalLabor++;
                    totalExpense++;

                    try {
                        console.log('🔵 POSTING LABOR:', JSON.stringify(laborRecord, null, 2));
                        await axios.post(`${SQL_API}/FINANCE_LaborHours`, laborRecord);
                        laborSuccess++;
                    } catch (err) {
                        console.error('❌ LABOR FAILED:', err.response?.data || err.message);
                        addLog(`⚠️ Labor failed: ${err.message}`, 'warning');
                    }

                    try {
                        console.log('🟢 POSTING EXPENSE:', JSON.stringify(expenseRecord, null, 2));
                        const response = await axios.post(`${SQL_API}/FINANCE_Expenses`, expenseRecord);
                        console.log('✅ SUCCESS:', response.data);
                        expenseSuccess++;
                    } catch (err) {
                        console.error('❌ EXPENSE FAILED:', err.response?.data || err.message);
                        addLog(`⚠️ Expense failed: ${err.message}`, 'warning');
                    }

                    setExportProgress({
                        current: laborSuccess + expenseSuccess,
                        total: totalLabor + totalExpense,
                        type: 'Records'
                    });
                }
            }

            addLog(`✅ Labor: ${laborSuccess}/${totalLabor}`, laborSuccess === totalLabor ? 'success' : 'warning');
            addLog(`✅ Expenses: ${expenseSuccess}/${totalExpense}`, expenseSuccess === totalExpense ? 'success' : 'warning');

            if (laborSuccess === totalLabor && expenseSuccess === totalExpense) {
                addLog('🎉 Export completed!', 'success');
                setActiveStep(3);

                setTimeout(() => {
                    setStatusDialog({ open: false, logs: [] });
                    setTimeout(() => {
                        setFile(null);
                        setEmployeeData([]);
                        setSelectionModel([]);
                        setSelectedCategory('');
                        setSelectedSubcategory('');
                        setActiveStep(0);
                    }, 2000);
                }, 3000);
            }

        } catch (error) {
            console.error('Export error:', error);
            addLog(`❌ Export failed: ${error.message}`, 'error');
        } finally {
            setLoading(false);
            setExportProgress({ current: 0, total: 0, type: '' });
        }
    };
    const columns = [
        {
            field: 'employeeName',
            headerName: 'Employee Name',
            flex: 1.5,
            minWidth: 200,
        },
        {
            field: 'nameKey',
            headerName: 'Employee Key',
            width: 140
        },
        {
            field: 'LOC',
            headerName: 'LOC Code',
            width: 100,
            renderCell: (params) => (
                <Chip label={params.value} size="small" variant="outlined" />
            )
        },
        {
            field: 'SchoolName',
            headerName: 'School',
            flex: 1.5,
            minWidth: 200,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1}>
                    {params.row.matched ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                        <ErrorIcon fontSize="small" color="error" />
                    )}
                    <Typography variant="body2" color={params.row.matched ? 'inherit' : 'error'}>
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        {
            field: 'totalHours',
            headerName: 'Total Hours',
            width: 130,
            type: 'number',
            valueFormatter: (params) => params.value?.toFixed(2) || '0.00'
        },
        {
            field: 'totalGross',
            headerName: 'Total Gross',
            width: 140,
            type: 'number',
            renderCell: (params) => (
                <Typography variant="body2" fontWeight="medium" color="success.main">
                    ${params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </Typography>
            )
        },
        {
            field: 'periodEndDate',
            headerName: 'Period End',
            width: 120
        },
        {
            field: 'matched',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => (
                params.value ? (
                    <Chip label="Matched" size="small" color="success" />
                ) : (
                    <Chip label="Unmatched" size="small" color="error" />
                )
            )
        }
    ];

    const CustomToolbar = () => (
        <GridToolbarContainer sx={{ p: 2, gap: 2 }}>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
            <GridToolbarExport />
            <Box sx={{ flexGrow: 1 }} />
            <GridToolbarQuickFilter />
        </GridToolbarContainer>
    );

    const steps = ['Upload PDF', 'Process Document', 'Select Categories', 'Export Data'];

    return (
        <Box sx={{ p: 3, maxWidth: '100%', mx: 'auto' }}>
            <Paper elevation={3} sx={{ p: 4, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <AutoAwesomeIcon sx={{ fontSize: 48 }} />
                    <Box>
                        <Typography variant="h3" fontWeight={700}>
                            AI Labor Hours Import
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.9 }}>
                            Automated payroll processing with document intelligence
                        </Typography>
                    </Box>
                </Stack>
            </Paper>

            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Paper>

            <Stack spacing={3}>
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <DescriptionIcon color="primary" />
                            <Typography variant="h6">Step 1: Upload Payroll PDF</Typography>
                            {schools.length > 0 && (
                                <Chip
                                    label={`${schools.length} schools loaded`}
                                    color="success"
                                    size="small"
                                    icon={<SchoolIcon />}
                                />
                            )}
                        </Stack>

                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                size="large"
                                disabled={loading}
                            >
                                Select PDF File
                                <input
                                    type="file"
                                    hidden
                                    accept=".pdf"
                                    onChange={handleFileSelect}
                                />
                            </Button>

                            {file && (
                                <>
                                    <Chip
                                        label={file.name}
                                        color="primary"
                                        onDelete={() => {
                                            setFile(null);
                                            setEmployeeData([]);
                                            setActiveStep(0);
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={processPayrollPDF}
                                        disabled={loading}
                                        startIcon={<RefreshIcon />}
                                        color="secondary"
                                    >
                                        Process Document
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </CardContent>
                </Card>

                {employeeData.length > 0 && (
                    <>
                        <Card variant="outlined">
                            <CardContent>
                                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                    <AttachMoneyIcon color="primary" />
                                    <Typography variant="h6">Step 2: Select Category & Subcategory</Typography>
                                </Stack>

                                <Alert severity="error" sx={{ mb: 2 }}>
                                    ⚠️ <strong>REQUIRED:</strong> You must select both Category and Subcategory before exporting
                                </Alert>

                                <Stack direction="row" spacing={2}>
                                    <FormControl fullWidth required error={!selectedCategory}>
                                        <InputLabel>Expense Category *</InputLabel>
                                        <Select
                                            value={selectedCategory}
                                            onChange={(e) => setSelectedCategory(e.target.value)}
                                            label="Expense Category *"
                                        >
                                            {categories.map(cat => (
                                                <MenuItem key={cat.CategoryID} value={cat.CategoryID}>
                                                    {cat.CategoryName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth required disabled={!selectedCategory} error={!selectedSubcategory}>
                                        <InputLabel>Expense Subcategory *</InputLabel>
                                        <Select
                                            value={selectedSubcategory}
                                            onChange={(e) => setSelectedSubcategory(e.target.value)}
                                            label="Expense Subcategory *"
                                        >
                                            {subcategories.map(sub => (
                                                <MenuItem key={sub.SubcategoryID} value={sub.SubcategoryID}>
                                                    {sub.SubcategoryName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            </CardContent>
                        </Card>

                        <Card variant="outlined">
                            <CardContent>
                                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                    <PersonIcon color="primary" />
                                    <Typography variant="h6">Step 3: Review Employee Data</Typography>
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Badge badgeContent={stats.total} color="primary">
                                        <Chip label="Total" />
                                    </Badge>
                                    <Badge badgeContent={stats.matched} color="success">
                                        <Chip label="Matched" color="success" />
                                    </Badge>
                                    <Badge badgeContent={stats.unmatched} color="error">
                                        <Chip label="Unmatched" color="error" />
                                    </Badge>
                                </Stack>

                                {stats.unmatched > 0 && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        ⚠️ {stats.unmatched} employee(s) could not be matched to schools. These will be excluded from export.
                                    </Alert>
                                )}

                                <Box sx={{ height: 600, width: '100%' }}>
                                    <DataGridPro
                                        rows={employeeData}
                                        columns={columns}
                                        checkboxSelection
                                        disableRowSelectionOnClick
                                        onRowSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                                        rowSelectionModel={selectionModel}
                                        slots={{ toolbar: CustomToolbar }}
                                        initialState={{
                                            pagination: { paginationModel: { pageSize: 25 } },
                                            sorting: { sortModel: [{ field: 'employeeName', sort: 'asc' }] },
                                        }}
                                        pageSizeOptions={[10, 25, 50, 100]}
                                        density="comfortable"
                                    />
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                <Stack direction="row" spacing={2} justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setEmployeeData([]);
                                            setFile(null);
                                            setSelectionModel([]);
                                            setSelectedCategory('');
                                            setSelectedSubcategory('');
                                            setActiveStep(0);
                                        }}
                                        size="large"
                                    >
                                        Clear All Data
                                    </Button>

                                    <Tooltip title={!selectedCategory || !selectedSubcategory ? 'Please select Category and Subcategory first' : ''}>
                                        <span>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="large"
                                                onClick={exportToDatabase}
                                                disabled={
                                                    loading ||
                                                    !selectedCategory ||
                                                    !selectedSubcategory ||
                                                    stats.matched === 0
                                                }
                                                startIcon={<SaveIcon />}
                                                sx={{ fontSize: '1.1rem', py: 1.5, px: 4 }}
                                            >
                                                Export to Database
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </CardContent>
                        </Card>
                    </>
                )}
            </Stack>

            <Dialog
                open={statusDialog.open}
                maxWidth="md"
                fullWidth
                onClose={() => !loading && setStatusDialog({ open: false, logs: [] })}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <AutoAwesomeIcon color="primary" />
                        <Typography variant="h6">Processing Status</Typography>
                    </Stack>
                    {!loading && (
                        <IconButton onClick={() => setStatusDialog({ open: false, logs: [] })}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </DialogTitle>

                <DialogContent>
                    {loading && (
                        <Box sx={{ mb: 2 }}>
                            <LinearProgress />
                            {exportProgress.total > 0 && (
                                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                                    {exportProgress.type}: {exportProgress.current} / {exportProgress.total}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                        {statusDialog.logs.map((log, idx) => (
                            <Alert
                                key={idx}
                                severity={log.type}
                                sx={{ mb: 1 }}
                                icon={
                                    log.type === 'success' ? <CheckCircleIcon /> :
                                        log.type === 'error' ? <ErrorIcon /> :
                                            log.type === 'warning' ? <WarningIcon /> : undefined
                                }
                            >
                                <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                                    {log.timestamp}
                                </Typography>
                                {log.message}
                            </Alert>
                        ))}
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setStatusDialog({ open: false, logs: [] })}
                        disabled={loading}
                        variant="contained"
                    >
                        {loading ? 'Processing...' : 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LaborHoursUpload;